import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaCalendarAlt, FaWallet, FaChartLine, FaArrowUp, FaArrowDown } from 'react-icons/fa';

const SellerPayments = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState('this_month');

    useEffect(() => {
        // Mock data
        setTimeout(() => {
            setPayments([
                { id: 'PAY001', date: '2024-12-10', orderId: 'ORD156', amount: 4599, fee: 230, net: 4369, status: 'completed' },
                { id: 'PAY002', date: '2024-12-08', orderId: 'ORD143', amount: 1899, fee: 95, net: 1804, status: 'completed' },
                { id: 'PAY003', date: '2024-12-05', orderId: 'ORD128', amount: 2499, fee: 125, net: 2374, status: 'completed' },
                { id: 'PAY004', date: '2024-12-15', orderId: 'ORD178', amount: 3299, fee: 165, net: 3134, status: 'pending' },
            ]);
            setLoading(false);
        }, 500);
    }, []);

    const totalEarnings = 156789;
    const pendingAmount = 12450;
    const lastPayout = 45600;

    return (
        <div className="p-6">
            <div className="mb-6 flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Payments</h1>
                    <p className="text-gray-600">Track your earnings and payouts</p>
                </div>
                <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                    <FaDownload /> Download Statement
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-lg text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-green-100 text-sm">Total Earnings</p>
                            <p className="text-3xl font-bold">₹{totalEarnings.toLocaleString()}</p>
                        </div>
                        <FaRupeeSign className="text-4xl opacity-50" />
                    </div>
                    <div className="mt-2 flex items-center gap-1 text-sm">
                        <FaArrowUp /> <span>12% from last month</span>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-6 rounded-lg text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-yellow-100 text-sm">Pending</p>
                            <p className="text-3xl font-bold">₹{pendingAmount.toLocaleString()}</p>
                        </div>
                        <FaWallet className="text-4xl opacity-50" />
                    </div>
                    <p className="mt-2 text-sm">Processing in 2-3 days</p>
                </div>

                <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-lg text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-blue-100 text-sm">Last Payout</p>
                            <p className="text-3xl font-bold">₹{lastPayout.toLocaleString()}</p>
                        </div>
                        <FaChartLine className="text-4xl opacity-50" />
                    </div>
                    <p className="mt-2 text-sm">Dec 5, 2024</p>
                </div>

                <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-lg text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-purple-100 text-sm">This Month</p>
                            <p className="text-3xl font-bold">₹28,450</p>
                        </div>
                        <FaCalendarAlt className="text-4xl opacity-50" />
                    </div>
                    <div className="mt-2 flex items-center gap-1 text-sm">
                        <FaArrowDown /> <span>8% from target</span>
                    </div>
                </div>
            </div>

            {/* Date Filter */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="flex gap-2">
                    {[
                        { value: 'this_week', label: 'This Week' },
                        { value: 'this_month', label: 'This Month' },
                        { value: 'last_month', label: 'Last Month' },
                        { value: 'last_3_months', label: 'Last 3 Months' },
                    ].map((option) => (
                        <button
                            key={option.value}
                            onClick={() => setDateRange(option.value)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${dateRange === option.value
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Transactions Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b">
                    <h2 className="text-lg font-semibold text-gray-800">Recent Transactions</h2>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Loading transactions...</div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fee</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Net</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {payments.map((payment) => (
                                <tr key={payment.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{payment.id}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">{payment.date}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-blue-600">{payment.orderId}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">₹{payment.amount.toLocaleString()}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-red-500">-₹{payment.fee}</td>
                                    <td className="px-6 py-4 whitespace-nowrap font-semibold text-green-600">₹{payment.net.toLocaleString()}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${payment.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                            {payment.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default SellerPayments;
